import { cn } from "design-system/utils"
import { Link2, Link2Off, Unlink } from "lucide-react"
import { getSupportEntityTimelineRowId } from "modules/support/lib/supportEntityMarker"
import { useTranslation } from "react-i18next"
import {
  ActionRequestStatusEnum,
  ConversationSupportActionRequestItemResponseDataSchema,
} from "schema"
import { z } from "zod"
import { EntityIconTooltip, EntityRow } from "./EntityRow"

type Props = {
  item: z.infer<typeof ConversationSupportActionRequestItemResponseDataSchema>
  isFirst: boolean
  isLast: boolean
  onClick: () => void
}

export function ActionRequestEntityRow({ item, isFirst, isLast, onClick }: Props) {
  return (
    <EntityRow
      id={getSupportEntityTimelineRowId(item.kind, item.id)}
      bulletClassName="bg-chart-3"
      text={item.summary}
      isFirst={isFirst}
      isLast={isLast}
      onClick={onClick}
      badge={<ActionRequestStatusIcon status={item.status} showDefaultIcon />}
    />
  )
}

export function ActionRequestStatusIcon({
  status,
  className,
  hasTooltip = true,
  showDefaultIcon = false,
}: {
  status: z.infer<typeof ConversationSupportActionRequestItemResponseDataSchema>["status"]
  className?: string
  hasTooltip?: boolean
  showDefaultIcon?: boolean
}) {
  const { t } = useTranslation()
  const label = t(`support.entities.actionRequestStatus.${status}`)
  let icon

  switch (status) {
    case ActionRequestStatusEnum.Pending:
      if (!showDefaultIcon) return null
      icon = <Link2Off size={14} className={cn("text-muted-foreground", className)} />
      break
    case ActionRequestStatusEnum.Fulfilled:
      icon = <Link2 size={14} className={cn("text-chart-3", className)} />
      break
    case ActionRequestStatusEnum.Dismissed:
      icon = <Unlink size={14} className={cn("text-muted-foreground", className)} />
      break
  }

  return hasTooltip ? <EntityIconTooltip label={label}>{icon}</EntityIconTooltip> : icon
}
